import { useEffect, useMemo, useState } from 'react';
import { CommonActions } from '@react-navigation/native';
import { StackHeaderProps } from '@react-navigation/stack';
import Constants from 'expo-constants';
import { Box, Divider, HStack, IconButton, Image, Pressable, useMediaQuery, useTheme } from 'native-base';
import FeatherIcons from 'react-native-vector-icons/Feather';
import { BotMessageSquare } from 'lucide-react';
import { find, isEmpty, map, pick, sortBy, startCase, toLower, values } from 'lodash';
import { useTranslation } from 'react-i18next';

import { Modal, NavBar, NavDropdown, SettingsIcon, SwitchHorizontalIcon, Text } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import { selectStoreConfig, selectUserProfile } from '@app/store/selectors';
import { storeFocusSlice } from '@app/store/slices/storeFocusSlice';
import { storeListSlice } from '@app/store/slices/storeListSlice';
import { userProfileSlice } from '@app/store/slices/userProfileSlice';
import { DropdownCultures } from '@app/features/app';
import { useSiteConfig } from '@app/features/store-core';
import { useLiveChat } from '@app/features/live-chat/store/chat.store';
import { useLiveChatPayload } from '@app/features/live-chat/hooks/use-live-chat-payload.hook';
import { DashboardModalType, useDashboard } from '@app/features/dashboard';
import { getSnapEngagePageModule, SNAP_ENGAGE_MODULE_LABELS } from '@app/hooks/use-snap-engage-context.hook';
import { NavSectionDto, NavSectionModuleDto, SiteSimpleDto } from '@pimm/services/lib/sms-tenants';
import { useModalFocus } from '@app/hooks/modal-focus.hook';
import { appConfigSlice } from '@app/store/slices/appConfigSlice';
import { SiteList } from '@app/features/store-core';
import { Clock, DayblockCountdown } from '../shared';

type HeaderProps = StackHeaderProps & { onChangeStore?: (siteId?: string) => void };

export const Header = ({ navigation, route }: HeaderProps) => {
  const { colors } = useTheme();
  const [isTabletScreen] = useMediaQuery({ maxWidth: 1200 });
  const dispatch = useAppDispatch();
  const userProfile = useAppSelector(selectUserProfile);
  const storeConfig = useAppSelector(selectStoreConfig);
  const { t } = useTranslation();

  const { translate, locale } = useAppLocale();
  const { toggleModal } = useDashboard();
  const { siteConfig } = useSiteConfig();
  const resourceLinks = siteConfig?.companyInfo?.brand?.resourceLinks;
  const { email: liveChatEmail, username: liveChatUsername, buildPayload: buildLiveChatPayload } = useLiveChatPayload();

  const dropdownItems = useMemo<{ accessorKey?: string; icon: React.ReactElement; text: string; url?: string }[]>(
    () => [
      { accessorKey: 'settings', icon: <SettingsIcon size="18px" />, text: 'app:settings' },
      ...sortBy(resourceLinks ?? [], 'seq').map(link => ({
        accessorKey: link.title,
        icon: link.imageUrl ? (
          <Image source={{ uri: link.imageUrl }} alt={link.title} size={4} resizeMode="contain" />
        ) : (
          <FeatherIcons name="external-link" size={4} />
        ),
        text: translate(link.title, link.titleTranslations),
        url: link.url,
      })),
    ],
    [resourceLinks, locale],
  );

  const [titleSection, setTitleSection] = useState<NavSectionDto>();
  const siteListModal = useModalFocus();
  const isSiteModalEnabled = (userProfile?.sites ?? [])?.length > 1;

  const availableCultures = siteConfig?.companyInfo?.brand?.availableCultures;
  const hasStore = !!siteConfig.config;
  const headerTitle = Constants.expoConfig?.extra?.name ?? '';

  const selectedSite = find(userProfile?.sites, _ => _.id === (siteConfig?.id ?? userProfile?.defaultSiteId));

  const fullName = values(pick(userProfile, ['firstName', 'lastName']))
    .filter(Boolean)
    .join(' ');

  const handleChangeStore = (store: SiteSimpleDto) => {
    if (siteConfig.id !== store.id) {
      navigation.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [{ name: 'Main', params: { screen: 'PositionGuide', siteId: store.id } }],
        }),
      );
    }
  };

  const handlePressChat = () => {
    const pageModule = getSnapEngagePageModule(window.location.pathname, SNAP_ENGAGE_MODULE_LABELS);
    if (!pageModule) return;

    useLiveChat.getState().open({
      email: liveChatEmail,
      username: liveChatUsername,
      payload: buildLiveChatPayload(pageModule),
    });
  };

  const handlePressMore = () => {
    dispatch(appConfigSlice.actions.toggleWidget());
  };

  const handlePressInfo = () => {
    toggleModal({ type: DashboardModalType.StoreConfig });
  };

  const handlePressDropdownItem = (accessorKey?: string) => () => {
    const item = find(dropdownItems, ['accessorKey', accessorKey]);
    let openUrl = item?.url;
    if (item?.accessorKey === 'settings') {
      toggleModal({ type: DashboardModalType.AccountSettings });
      return;
    }
    if (openUrl) window.open(openUrl, '_blank');
  };

  const handlePressLogout = () => {
    localStorage.removeItem('token');
    // clear logged-in user information
    dispatch(storeListSlice.actions.clear());
    dispatch(storeFocusSlice.actions.clear());
    dispatch(userProfileSlice.actions.clear());
    // close and tear down the support chat widget so it doesn't linger on the login screen
    useLiveChat.getState().reset();
    navigation.navigate('Login');
  };

  useEffect(() => {
    if (storeConfig?.companyInfo?.brand || siteConfig.companyInfo?.brand) {
      const brand = siteConfig.companyInfo?.brand ?? storeConfig?.companyInfo?.brand;
      const platform = brand?.navSections?.find(_ => _.appType?.toLowerCase() === 'dop');
      const title = platform?.sections?.reduce((_title: NavSectionModuleDto | undefined, _section) => {
        if (_title) return _title;
        return _section.modules?.find(_ => _.identifier?.toLowerCase() === 'doptitle');
      }, undefined);

      setTitleSection(title);
    }
  }, [storeConfig?.companyInfo?.brand, siteConfig.companyInfo?.brand]);

  return (
    <NavBar
      _container={isTabletScreen ? { pr: 0 } : undefined}
      title={
        <HStack alignItems="center">
          <Box alignItems="center" w={{ md: '60px', xl: '70px' }}>
            <Image
              alt={translate(titleSection?.title ?? headerTitle, titleSection?.titleTranslations)}
              source={{
                uri: titleSection?.iconUrl,
              }}
              rounded="full"
              resizeMode="contain"
              resizeMethod="scale"
              size={10}
            />
          </Box>
          <Box maxWidth={{ base: 400, xl: undefined }}>
            <Text color="black" size="xl" fontWeight={700} numberOfLines={2} lineHeight="xs">
              {translate(titleSection?.title ?? headerTitle, titleSection?.titleTranslations)}
            </Text>
          </Box>
        </HStack>
      }
      more={
        isTabletScreen ? (
          <Pressable pr={1.5} alignItems="center" justifyContent="center" h="full" onPress={handlePressMore}>
            <FeatherIcons name="more-vertical" color="white" size={22} />
          </Pressable>
        ) : undefined
      }
      rightComponent={
        <HStack space={{ base: 2, xl: 3 }} alignItems="center">
          {!isTabletScreen && hasStore && (
            <Clock px={2} rounded="md" borderWidth={1} borderColor="black" _text={{ size: 'xl', fontWeight: 700, color: 'black' }} />
          )}

          {hasStore && (
            <>
              <DayblockCountdown />
              <Divider height="22px" bg="gray.50" orientation="vertical" />
            </>
          )}

          {!isEmpty(availableCultures) && <DropdownCultures variant="contained" cultures={availableCultures} />}

          {hasStore && (
            <Pressable
              p="6px"
              rounded="8px"
              alignItems="center"
              justifyContent="center"
              onPress={handlePressChat}
              accessibilityLabel="Support Chat"
            >
              <BotMessageSquare size={28} strokeWidth={1.8} color={colors.gray[800]} />
            </Pressable>
          )}

          <NavDropdown
            mr={3}
            accessibilityLabel="Account Dropdown"
            t={t}
            person={{ name: startCase(toLower(fullName)), email: userProfile?.contactEmail }}
            onPressLogout={handlePressLogout}
          >
            {hasStore &&
              map(dropdownItems, item => {
                return (
                  <NavDropdown.Item
                    key={item.accessorKey}
                    icon={item.icon}
                    text={t(item.text)}
                    onPress={handlePressDropdownItem(item.accessorKey)}
                  />
                );
              })}
          </NavDropdown>
        </HStack>
      }
    >
      {!!selectedSite && (
        <HStack space={1} alignItems="center" w={{ base: 280, xl: 360 }}>
          <Box flex={1}></Box>
          <Box rounded="lg" w={{ base: 240, xl: 300 }} bg="white">
            <Pressable flex={1} onPress={siteListModal.setOpen} isDisabled={!isSiteModalEnabled}>
              <HStack space={2} alignItems="center" justifyContent="center" px={3} h={8} rounded="md">
                <Text
                  color="black"
                  size="lg"
                  textTransform="uppercase"
                  textAlign="center"
                  fontWeight={700}
                  lineHeight="xs"
                  numberOfLines={1}
                >
                  {selectedSite?.name ?? ''}
                </Text>
                {isSiteModalEnabled && (
                  <Box position="absolute" right={2}>
                    <SwitchHorizontalIcon size={4} color="black" />
                  </Box>
                )}
              </HStack>
            </Pressable>
          </Box>
          <Box flex={1}>
            <IconButton rounded="full" p={0} icon={<FeatherIcons name="info" size={20} color="black" />} onPress={handlePressInfo} />
          </Box>
        </HStack>
      )}

      <Modal
        size="xl"
        hideClose
        noPadding
        _content={{ maxWidth: { md: '95%', lg: '90%' }, h: '80%', maxHeight: 680 }}
        isOpen={siteListModal.isOpen}
      >
        <SiteList siteId={selectedSite?.id} onClose={siteListModal.setHide} onSelectSite={handleChangeStore} />
      </Modal>
    </NavBar>
  );
};
